import { container, TYPES } from '../inversify.config';
import type { IFileHandlerService } from '../interface/IFileHandlerService';
import type { IHttpWrapperService } from '../interface/IHttpWrapperService';
import type { FileMetadata, UploadCustomerDocumentRequest, CustomerDocumentMetadata, DocumentActionRequest } from '../../models/fileModel';
import type { AxiosResponse } from 'axios';
import type { ApiResponse } from '../../models/ApiResponse';
import { isSuccessResponse } from '../../models/ApiResponse';

/**
 * Service for managing file operations
 */
export class FileHandlerService implements IFileHandlerService {
  private httpService: IHttpWrapperService = container.get(TYPES.IHttpWrapperService);

  /**
   * Fetches file metadata by file ID
   * @param id - The unique identifier for the file
   * @returns Promise<FileMetadata> - The file metadata
   */
  public getfiledetails = async (id: string): Promise<FileMetadata> => {
    try {
      const response: AxiosResponse<ApiResponse<FileMetadata>> = await this.httpService.getRequest<FileMetadata>(`file/metadata/${id}`);

      if (isSuccessResponse(response.data)) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || `Failed to fetch file metadata for id ${id}`);
      }
    } catch (error) {
      throw error;
    }
  };

  /**
   * Downloads a file for the specified customer
   * @param customerId - The unique identifier for the customer
   * @returns Promise<Blob> - The file data as a blob
   */
  public downloadFile = async (customerId: string): Promise<Blob> => {
    try {
      // Use the new postBlobRequest method to handle memory stream directly
      const response: AxiosResponse<Blob> = await this.httpService.postBlobRequest<{ CustomerId: string }>(
        'file/download',
        { CustomerId: customerId }
      );

      // Return the blob directly from the memory stream
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Uploads customer documents
   * @param request - The upload request containing customer ID, file, and metadata
   * @returns Promise<void>
   */
  public uploadCustomerDocument = async (request: UploadCustomerDocumentRequest): Promise<void> => {
    try {
      const formData = new FormData();
      formData.append('CustomerId', request.customerId);
      formData.append('CreatedBy', request.createdBy);
      formData.append('PartnerCode', request.partnerCode);
      formData.append('DocumentType', request.documentType);
      formData.append('Documents', request.file);

      console.log('Uploading document with data:', {
        customerId: request.customerId,
        createdBy: request.createdBy,
        partnerCode: request.partnerCode,
        documentType: request.documentType,
        fileName: request.file.name,
        fileSize: request.file.size,
        fileType: request.file.type,
      });

      const response: AxiosResponse<ApiResponse<null>> = await this.httpService.postFormDataRequest<null>(
        'file/customerdocument/upload',
        formData
      );

      console.log('Upload response:', response.data);
      console.log('Response status:', response.status);

      // Check if response is successful (200-299 status code)
      if (response.status >= 200 && response.status < 300) {
        // Special case: Backend returns isSuccess: false even on success
        // Check if the message indicates success
        const successMessages = ['uploaded successfully', 'upload successful', 'success'];
        const messageIndicatesSuccess = response.data.message && 
          successMessages.some(msg => response.data.message?.toLowerCase().includes(msg));

        if (response.data.isSuccess === false && !messageIndicatesSuccess) {
          console.error('Upload failed with response:', response.data);
          throw new Error(response.data.message || 'Failed to upload document');
        }
        
        console.log('Upload successful');
        return;
      }

      // If we get here, something unexpected happened
      console.error('Unexpected response:', response);
      throw new Error('Unexpected response from server');
    } catch (error) {
      console.error('Upload error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to upload document');
    }
  };

  /**
   * Retrieves customer document metadata
   * @param customerId - The unique identifier for the customer
   * @returns Promise<CustomerDocumentMetadata[]> - Array of document metadata
   */
  public getCustomerDocumentMetadata = async (customerId: string): Promise<CustomerDocumentMetadata[]> => {
    try {
      const response: AxiosResponse<ApiResponse<CustomerDocumentMetadata[]>> = await this.httpService.getRequest<CustomerDocumentMetadata[]>(
        `file/customerdocument/metadata/${customerId}`
      );

      if (isSuccessResponse(response.data)) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || `Failed to fetch document metadata for customer ${customerId}`);
      }
    } catch (error) {
      throw error;
    }
  };

  /**
   * Downloads a customer document
   * @param request - The document action request containing customerId and documentName
   * @returns Promise<Blob> - The file blob
   */
  public downloadCustomerDocument = async (request: DocumentActionRequest): Promise<Blob> => {
    try {
      const response = await this.httpService.postBlobRequest(
        'file/customerdocument/download',
        request
      );

      return response.data;
    } catch (error) {
      console.error('Download error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to download document');
    }
  };

  /**
   * Deletes a customer document
   * @param request - The document action request containing customerId and documentName
   * @returns Promise<void>
   */
  public deleteCustomerDocument = async (request: DocumentActionRequest): Promise<void> => {
    try {
      const response: AxiosResponse<ApiResponse<null>> = await this.httpService.deleteRequest<null, DocumentActionRequest>(
        'file/customerdocument/delete',
        request
      );

      if (!isSuccessResponse(response.data)) {
        throw new Error(response.data.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Delete error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to delete document');
    }
  };

  /**
   * Downloads export zip file for partner's DB XML data
   * @param partnerCode - The partner code to export data for
   * @param names - Optional entity name(s) to export (comma-separated). If omitted, exports all entities.
   * @returns Promise<void>
   */
  public downloadExportZip = async (partnerCode: string, names?: string): Promise<void> => {
    try {
      // Build query string
      const params: string[] = [];
      if (names && names.trim().length > 0) {
        params.push(`names=${encodeURIComponent(names)}`);
      }
      params.push(`partnerCode=${encodeURIComponent(partnerCode)}`);
      const url = `export/master/exportzip?${params.join('&')}`;

      console.log('Downloading export zip:', { partnerCode, names, url });

      // Use getBlobRequest for binary download
      const response: AxiosResponse<Blob> = await this.httpService.getBlobRequest(url);

      // Extract filename from Content-Disposition header or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `export_${partnerCode}.zip`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]]/g, '');
        }
      }

      // If names were specified, include in filename
      if (names && names.trim().length > 0) {
        const entityName = names.split(',')[0]; // Use first entity name
        filename = `export_${entityName}_${partnerCode}.zip`;
      }

      // Trigger browser download using blob URL
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      console.log('Export downloaded successfully:', filename);
    } catch (error) {
      console.error('Export download error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to download export zip');
    }
  };

  /**
   * Pushes master data to OTM for the specified partner
   * @param partnerCode - The partner code to push data for
   * @returns Promise with API response containing success status and details
   */
  public pushToOTM = async (partnerCode: string): Promise<{ success: boolean; statusCode: number; count: number; response: string }> => {
    try {
      const url = `export/master/import/otm?partnerCode=${encodeURIComponent(partnerCode)}`;
      
      console.log('Pushing to OTM:', { partnerCode, url });

      const response: AxiosResponse<ApiResponse<{ success: boolean; statusCode: number; count: number; response: string }>> = await this.httpService.postRequest(
        url,
        {} // Empty body for POST request
      );

      console.log('Push to OTM response:', response.data);
      
      // Check if wrapped in ApiResponse or returned directly
      if (isSuccessResponse(response.data) && response.data.data) {
        return response.data.data;
      } else if ((response.data as any).success !== undefined) {
        // API returns data directly without ApiResponse wrapper
        return response.data as any;
      } else {
        throw new Error('Failed to push to OTM');
      }
    } catch (error) {
      console.error('Push to OTM error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to push to OTM');
    }
  };

  public pushEntityToOTM = async (partnerCode: string, entityName: string): Promise<{ success: boolean; statusCode: number; count: number; response: string }> => {
    try {
      const url = `export/master/import/otm?name=${encodeURIComponent(entityName)}&partnerCode=${encodeURIComponent(partnerCode)}`;
      
      console.log('Pushing entity to OTM:', { partnerCode, entityName, url });

      const response: AxiosResponse<ApiResponse<{ success: boolean; statusCode: number; count: number; response: string }>> = await this.httpService.postRequest(
        url,
        {} // Empty body for POST request
      );

      console.log('Push entity to OTM response:', response.data);
      
      // Check if wrapped in ApiResponse or returned directly
      if (isSuccessResponse(response.data) && response.data.data) {
        console.log('Returning wrapped data:', response.data.data);
        return response.data.data;
      } else if ((response.data as any).success !== undefined) {
        // API returns data directly without ApiResponse wrapper
        console.log('Returning direct data:', response.data);
        return response.data as any;
      } else {
        console.log('Failed - no valid data structure');
        throw new Error(`Failed to push ${entityName} to OTM`);
      }
    } catch (error) {
      console.error('Push entity to OTM error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Failed to push ${entityName} to OTM`);
    }
  };
}